import React from "react";
import {
  AlertTriangle, RefreshCw, CheckCircle2, Clock, Play, MapPin, Image as ImageIcon,
  Pause, Loader2, X, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

const STATUS_FILTERS = [
  { k: "open", label: "Åbne" },
  { k: "in_progress", label: "I gang" },
  { k: "resolved", label: "Afsluttet" },
  { k: "all", label: "Alle" },
];

const STATUS_PILL = {
  open:        { color: "bg-red-100 text-red-900 border-red-300",        label: "Åben",     icon: AlertTriangle },
  in_progress: { color: "bg-amber-100 text-amber-900 border-amber-300",  label: "I gang",   icon: Clock },
  resolved:    { color: "bg-emerald-100 text-emerald-900 border-emerald-300", label: "Afsluttet", icon: CheckCircle2 },
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function DriverProblemsPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("open");
  const [updating, setUpdating] = React.useState(null);
  const [photoOpen, setPhotoOpen] = React.useState(null);
  const [noteDrafts, setNoteDrafts] = React.useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === "all" ? "/admin/problem-reports" : `/admin/problem-reports?status=${filter}`;
      const { data: r } = await adminApi().get(url);
      setData(r);
    } catch (e) {
      toast.error("Kunne ikke hente problem-rapporter");
    } finally { setLoading(false); }
  }, [filter]);

  React.useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status, admin_note) => {
    setUpdating(id);
    try {
      await adminApi().patch(`/admin/problem-reports/${id}`, { status, admin_note });
      toast.success(`Rapport markeret som "${STATUS_PILL[status]?.label || status}"`);
      await load();
    } catch (e) {
      toast.error("Kunne ikke opdatere");
    } finally { setUpdating(null); }
  };

  const unpauseDriver = async (driverId, name) => {
    if (!window.confirm(`Genåbn ${name} for nye opgaver?`)) return;
    try {
      await adminApi().post(`/admin/drivers/${driverId}/unpause`, { reason: "Manuelt genåbnet fra problem-rapporter" });
      toast.success(`${name} er genåbnet`);
      await load();
    } catch (e) {
      toast.error("Kunne ikke genåbne chauffør");
    }
  };

  if (loading && !data) {
    return <div className="bg-white border border-brand p-6 text-sm text-brand-text2" data-testid="problems-loading">Indlæser…</div>;
  }
  if (!data) return null;

  const counts = data.counts || {};

  return (
    <div className="space-y-4" data-testid="driver-problems-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
            <AlertTriangle size={22} className="text-brand-red" />
            Chauffør-problemer
          </h2>
          <p className="text-xs text-brand-text2 mt-1">
            Rapporter fra chauffører i felten. Akutte (bilen i stykker / uheld / høj alvor) auto-pauser chaufføren.
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center gap-1 transition-colors flex-shrink-0"
          data-testid="problems-refresh"
        >
          <RefreshCw size={12} /> Opdater
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Åbne" value={counts.open || 0} accent={counts.open ? "text-red-700" : "text-emerald-700"} icon={<AlertTriangle size={14} className="text-red-700" />} active={filter === "open"} onClick={() => setFilter("open")} testid="problems-tile-open" />
        <Tile label="I gang" value={counts.in_progress || 0} accent="text-amber-700" icon={<Clock size={14} className="text-amber-700" />} active={filter === "in_progress"} onClick={() => setFilter("in_progress")} testid="problems-tile-progress" />
        <Tile label="Afsluttet" value={counts.resolved || 0} accent="text-emerald-700" icon={<CheckCircle2 size={14} className="text-emerald-700" />} active={filter === "resolved"} onClick={() => setFilter("resolved")} testid="problems-tile-resolved" />
        <Tile label="Pausede chauffører" value={counts.auto_paused_drivers || 0} accent={counts.auto_paused_drivers ? "text-red-700" : "text-emerald-700"} icon={<Pause size={14} className="text-red-700" />} testid="problems-tile-paused" />
      </div>

      <div className="flex items-center gap-1 border-b border-brand">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`problems-filter-${f.k}`}
            className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              filter === f.k ? "border-brand-red text-brand-red" : "border-transparent text-brand-text2 hover:text-brand-dark"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {data.items.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-300 p-6 text-sm text-emerald-900 text-center flex items-center justify-center gap-2" data-testid="problems-empty">
          <CheckCircle2 size={16} />
          {filter === "open" ? "Ingen åbne problemer — perfekt!" : "Ingen rapporter i denne kategori"}
        </div>
      ) : (
        <div className="bg-white border border-brand divide-y divide-brand" data-testid="problems-list">
          {data.items.map((p) => {
            const cfg = STATUS_PILL[p.status] || STATUS_PILL.open;
            const Icon = cfg.icon;
            const isSevere = p.severity === "high" || ["car_broken", "accident"].includes(p.category);
            return (
              <div key={p.id} className="p-4" data-testid={`problem-${p.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs px-2 py-0.5 border ${cfg.color} font-bold uppercase flex items-center gap-1`}>
                        <Icon size={11} /> {cfg.label}
                      </span>
                      <span className={`text-xs px-2 py-0.5 font-bold uppercase ${
                        isSevere ? "bg-red-700 text-white" : "bg-slate-200 text-slate-800"
                      }`}>
                        {p.severity === "high" ? "AKUT" : p.severity === "medium" ? "MELLEM" : "LAV"}
                      </span>
                      {p.auto_paused_driver && (
                        <span className="text-xs bg-red-100 text-red-900 border border-red-300 px-2 py-0.5 font-bold uppercase flex items-center gap-1">
                          <Pause size={11} /> Auto-pauset
                        </span>
                      )}
                    </div>
                    <div className="font-display font-bold text-base text-brand-dark">{p.category_label}</div>
                    <div className="text-sm text-brand-text2 mt-0.5">
                      <b>{p.driver_name}</b> · {p.driver_email}
                      {p.booking_number && <> · booking <span className="font-mono">{p.booking_number}</span></>}
                    </div>
                    {p.note && (
                      <div className="mt-2 bg-brand-bg/50 border-l-2 border-brand-red px-3 py-2 text-sm text-brand-dark whitespace-pre-wrap">
                        {p.note}
                      </div>
                    )}
                    {(p.lat != null && p.lng != null) && (
                      <a
                        href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-brand-text2 hover:text-brand-red mt-2 inline-flex items-center gap-1"
                      >
                        <MapPin size={11} /> Vis lokation
                      </a>
                    )}
                    {p.photo && (
                      <button
                        onClick={() => setPhotoOpen(p.photo)}
                        className="ml-3 text-xs text-brand-text2 hover:text-brand-red mt-2 inline-flex items-center gap-1"
                        data-testid={`problem-view-photo-${p.id}`}
                      >
                        <ImageIcon size={11} /> Vis foto
                      </button>
                    )}
                    <div className="text-xs text-brand-text2 mt-1">Modtaget: {fmt(p.created_at)}</div>
                    {p.admin_note && (
                      <div className="mt-2 text-xs text-emerald-700 border-l-2 border-emerald-500 pl-2">
                        <b>Admin-note:</b> {p.admin_note}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5 flex-shrink-0 min-w-[140px]">
                    {p.status === "open" && (
                      <button
                        onClick={() => updateStatus(p.id, "in_progress")}
                        disabled={updating === p.id}
                        className="text-xs border border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white px-3 py-2 flex items-center justify-center gap-1 transition-colors disabled:opacity-60"
                        data-testid={`problem-progress-${p.id}`}
                      >
                        {updating === p.id ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
                        Påbegynd
                      </button>
                    )}
                    {p.status !== "resolved" && (
                      <button
                        onClick={() => updateStatus(p.id, "resolved", noteDrafts[p.id])}
                        disabled={updating === p.id}
                        className="text-xs bg-emerald-700 text-white hover:bg-emerald-800 px-3 py-2 flex items-center justify-center gap-1 transition-colors disabled:opacity-60"
                        data-testid={`problem-resolve-${p.id}`}
                      >
                        {updating === p.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                        Afslut
                      </button>
                    )}
                    {p.auto_paused_driver && (
                      <button
                        onClick={() => unpauseDriver(p.driver_id, p.driver_name)}
                        className="text-xs border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center justify-center gap-1 transition-colors"
                        data-testid={`problem-unpause-${p.id}`}
                      >
                        Genåbn chauffør
                      </button>
                    )}
                  </div>
                </div>

                {p.status !== "resolved" && (
                  <div className="mt-3">
                    <input
                      type="text"
                      placeholder="Admin-note (valgfri) — gemmes når du trykker Afslut"
                      value={noteDrafts[p.id] || ""}
                      onChange={(e) => setNoteDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                      className="w-full border border-brand px-2 py-1.5 text-xs focus:outline-none focus:border-brand-dark"
                      data-testid={`problem-note-input-${p.id}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Photo lightbox */}
      {photoOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setPhotoOpen(null)} data-testid="problem-photo-lightbox">
          <button className="absolute top-4 right-4 text-white" onClick={() => setPhotoOpen(null)}>
            <X size={28} />
          </button>
          <img src={photoOpen} alt="Problem-foto" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, accent, icon, onClick, active, testid }) {
  const clickable = !!onClick;
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={`text-left bg-white border p-4 transition-colors ${active ? "border-brand-dark shadow-sm" : "border-brand"} ${clickable ? "hover:border-brand-dark cursor-pointer" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="kicker">{label}</div>
        {icon}
      </div>
      <div className={`mt-2 text-3xl font-display font-bold ${accent}`}>{value}</div>
    </Comp>
  );
}
